export interface SpriteFrameInfo {
  textureKey: string;
  frame?: string | number | undefined;
  path?: string | undefined;
  width: number;
  height: number;
  /** Sheet cell size when the texture is a spritesheet; undefined for a single image. */
  frameWidth?: number | undefined;
  frameHeight?: number | undefined;
  /** Vertical origin: 1 stands the sprite on its tile, 0.5 centres it in the diamond. */
  anchorY: number;
  isPlaceholder: boolean;
}

export interface SpritePose {
  path?: string;
  textureKey?: string;
  width?: number;
  height?: number;
  frames?: number;
  frame?: number;
  frameWidth?: number;
  frameHeight?: number;
  anchorY?: number;
}

export interface SpriteManifestEntry {
  appearance: string;
  poses: Record<string, SpritePose>;
  /** Painted clips are additive; the frozen isometric views retain their poses. */
  animation?: ActorAnimation;
}

export interface ActorClip {
  frames: readonly number[];
  /** Authored views preserve equipment handedness and the direction of a strike. */
  facings?: Readonly<Record<string, readonly number[]>>;
  fps: number;
  loop: boolean;
}
export interface ActorAnimation {
  path: string;
  cell: { width: number; height: number };
  foot: readonly [number, number];
  walkFacings: readonly string[];
  clips: Record<string, ActorClip>;
}

export interface SpriteManifest {
  version: number;
  sprites: Record<string, SpriteManifestEntry>;
}

/** The frozen art manifest as written by recipes/art/pack.py (assets/manifests/slice.json). */
export interface SliceManifest {
  version: number;
  assets: readonly {
    recipeId: string;
    class: string;
    packed: { path: string; size: readonly number[] };
    animation?: ActorAnimation;
    geometry?: {
      cell?: { width: number; height: number };
      sheet?: { grid: readonly number[]; cellFacings: readonly (string | null)[] };
      /** Rows from a cell's top to the figure's lowest opaque pixel, measured by pack.py. */
      footY?: number;
    };
  }[];
}

/**
 * Translates the frozen slice manifest into renderable sprite entries.
 *
 * Ground tiles sit centred in their isometric diamond; walls, props and actors
 * stand on it. Actor sheets are 8-facing compass rotations with an empty centre
 * cell, so each non-null `cellFacings` entry becomes its own pose and `idle`
 * aliases the south (viewer-facing) cell.
 */
export function spriteManifestFromSlice(slice: SliceManifest, assetBase = ''): SpriteManifest {
  const sprites: Record<string, SpriteManifestEntry> = {};

  for (const asset of slice.assets) {
    const [width = 0, height = 0] = asset.packed.size;
    const path = `${assetBase}${asset.packed.path}`;
    const sheet = asset.geometry?.sheet;
    const cell = asset.geometry?.cell;
    // A ground diamond is centred in its tile; everything else stands on it. For a
    // rotation sheet "standing on it" is the measured foot line, not the bottom of
    // the cell: the generator's bottom padding would otherwise hold the figure that
    // many pixels off the floor, away from its own contact shadow.
    const anchorY = asset.recipeId.startsWith('tile-ground')
      ? 0.5
      : (asset.geometry?.footY && cell ? asset.geometry.footY / cell.height : 1);
    const poses: Record<string, SpritePose> = {};

    if (sheet && cell) {
      const base = { path, textureKey: asset.recipeId, width: cell.width, height: cell.height, frameWidth: cell.width, frameHeight: cell.height, anchorY };
      sheet.cellFacings.forEach((facing, index) => {
        if (facing) poses[facing] = { ...base, frame: index };
      });
      const south = sheet.cellFacings.indexOf('s');
      poses.idle = { ...base, frame: south >= 0 ? south : 0 };
    } else {
      poses.idle = { path, textureKey: asset.recipeId, width, height, anchorY };
    }

    sprites[asset.recipeId] = { appearance: asset.recipeId, poses,
      ...(asset.animation ? { animation: { ...asset.animation, path: `${assetBase}${asset.animation.path}` } } : {}) };
  }

  return { version: slice.version, sprites };
}

export const DEFAULT_SPRITE_MANIFEST: SpriteManifest = {
  version: 1,
  sprites: {
    'test-tile': {
      appearance: 'test-tile',
      poses: {
        idle: { textureKey: 'tile', width: 64, height: 32, path: '/assets/tile.svg' },
      },
    },
    'disgraced-legionary': {
      appearance: 'disgraced-legionary',
      poses: {
        idle: { textureKey: 'pc-disgraced-legionary-idle', width: 48, height: 64 },
        walk: { textureKey: 'pc-disgraced-legionary-walk', width: 48, height: 64 },
        attack: { textureKey: 'pc-disgraced-legionary-attack', width: 48, height: 64 },
      },
    },
    'unpaid-sellsword': {
      appearance: 'unpaid-sellsword',
      poses: {
        idle: { textureKey: 'pc-unpaid-sellsword-idle', width: 48, height: 64 },
        walk: { textureKey: 'pc-unpaid-sellsword-walk', width: 48, height: 64 },
      },
    },
    'wall-stone': {
      appearance: 'wall-stone',
      poses: {
        idle: { textureKey: 'wall-stone', width: 64, height: 96 },
      },
    },
    'stair-ramp': {
      appearance: 'stair-ramp',
      poses: {
        idle: { textureKey: 'stair-ramp', width: 64, height: 48 },
      },
    },
  },
};

export class SpriteManifestRegistry {
  private manifest: SpriteManifest;

  constructor(initialManifest: SpriteManifest = DEFAULT_SPRITE_MANIFEST) {
    this.manifest = { ...initialManifest, sprites: { ...initialManifest.sprites } };
  }

  register(entry: SpriteManifestEntry): void {
    this.manifest.sprites[entry.appearance] = entry;
  }

  /** Every registered entry, for texture preloading. */
  entries(): readonly SpriteManifestEntry[] {
    return Object.values(this.manifest.sprites);
  }

  resolve(appearance: string, pose = 'idle'): SpriteFrameInfo {
    const entry = this.manifest.sprites[appearance];
    const poseKey = entry && entry.poses[pose] ? pose : Object.keys(entry?.poses ?? {})[0];

    if (entry && poseKey) {
      const p = entry.poses[poseKey]!;
      return {
        textureKey: p.textureKey ?? `${appearance}-${poseKey}`,
        frame: p.frame,
        path: p.path,
        width: p.width ?? 48,
        height: p.height ?? 64,
        frameWidth: p.frameWidth,
        frameHeight: p.frameHeight,
        anchorY: p.anchorY ?? 1,
        isPlaceholder: false,
      };
    }

    // Graceful placeholder fallback for unmanifested appearances
    return {
      textureKey: `placeholder-${appearance}`,
      width: 48,
      height: 64,
      anchorY: 1,
      isPlaceholder: true,
    };
  }

  hasAppearance(appearance: string): boolean {
    return appearance in this.manifest.sprites;
  }

  animation(appearance: string): ActorAnimation | undefined {
    return this.manifest.sprites[appearance]?.animation;
  }
}
